import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/hooks/use-toast';
import { Users, Search, Plus, Edit, Trash2, ArrowLeft, BookOpen } from 'lucide-react';
import { SidebarProvider } from "@/components/ui/sidebar";
import Teacherheader from "./Teacherheader";
import Teachersidebar from "./Teachersidebar";
import SuspensionWarning from "./SuspensionWarning";
import useSuspensionCheck from "./useSuspensionCheck";
import { db, auth } from "../firebase/config";
import {
  collection,
  doc,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult, IdTokenResult } from "firebase/auth";
import { useNavigate } from 'react-router-dom';

interface Student {
  id: string;
  name: string;
  regNumber: string;
  formClass?: string;
  currentClassUid?: string;
  enrolledSubjects?: string[];
  form?: string;
}

interface StudentGroup {
  id: string;
  name: string;
  subject: string;
  form: string;
  studentIds: string[];
  createdAt?: any;
  createdBy?: string;
}

interface ClassItem {
  id: string;
  name: string;
  classUid: string;
  level: number;
  stream: string;
}

interface Subject {
  subjectId: string;
  name: string;
  code: string;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface CustomClaims {
  affiliations?: Affiliation[];
  defaultAffiliation?: Affiliation;
  schoolID?: string;
}

const GroupsManagement = () => {
  const navigate = useNavigate();
  
  // Check suspension status
  const { isSuspended, isLoading: suspensionLoading, schoolName: suspendedSchoolName } = useSuspensionCheck();

  const [savedGroups, setSavedGroups] = useState<StudentGroup[]>([]);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<StudentGroup | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<StudentGroup | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Form fields
  const [groupName, setGroupName] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedForm, setSelectedForm] = useState('');
  const [allFormStudents, setAllFormStudents] = useState<Student[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [studentSearchQuery, setStudentSearchQuery] = useState('');
  
  // Firebase states
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [forms, setForms] = useState<string[]>([]);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [schoolID, setSchoolID] = useState("");
  const [userID, setUserID] = useState("");
  const [isFetching, setIsFetching] = useState(false);

  // Authentication
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsAuthLoading(false);
        return;
      }

      setUserID(user.uid);
      try {
        const idTokenResult: IdTokenResult = await getIdTokenResult(user, true);
        const claims = idTokenResult.claims as CustomClaims;
        const affiliations: Affiliation[] = claims.affiliations || [];
        const defaultAff: Affiliation | undefined = claims.defaultAffiliation;
        const claimSchoolID: string | undefined = claims.schoolID;

        let selectedAffiliation: Affiliation | null = null;
        if (defaultAff && defaultAff.role === "school_staff" && defaultAff.schoolID) {
          selectedAffiliation = defaultAff;
        } else if (affiliations.length > 0) {
          selectedAffiliation = affiliations.find(aff => aff.role === "school_staff" && aff.schoolID) || affiliations[0];
        }

        if (selectedAffiliation?.role === "school_staff" && selectedAffiliation.schoolID && claimSchoolID === selectedAffiliation.schoolID) {
          setSchoolID(selectedAffiliation.schoolID);
          setHasValidClaims(true);
        } else {
          setHasValidClaims(false);
          setSchoolID("");
          toast({
            title: "Access Denied",
            description: "Invalid teacher permissions. Contact admin.",
            variant: "destructive",
          });
        }
      } catch (error: any) {
        console.error("Claims validation error:", error);
        toast({
          title: "Authentication Error",
          description: "Failed to verify permissions. Please log in again.",
          variant: "destructive",
        });
        setHasValidClaims(false);
        setSchoolID("");
      }
      setIsAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Fetch data
  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;

    const fetchData = async () => {
      try {
        // Fetch classes
        const classesCollection = collection(db, `schools/${schoolID}/classes`);
        const classesSnapshot = await getDocs(classesCollection);
        const classesData: ClassItem[] = classesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          classUid: doc.data().classUid || doc.id,
        } as ClassItem)).filter(c => c.name && c.name.trim() !== "");

        setClasses(classesData);

        // Derive forms
        const extractFormLevel = (className: string): string => {
          const parts = className.trim().toLowerCase().split(/\s+/);
          if (parts.length >= 3 && parts[0] === 'pre' && parts[1] === 'primary') {
            return className.split(/\s+/).slice(0, 3).join(' ');
          } else if (parts.length >= 2 && parts[0] === 'form') {
            return className.split(/\s+/).slice(0, 2).join(' ');
          } else {
            return className.split(/\s+/).slice(0, 1).join(' ');
          }
        };
        const uniqueForms = [...new Set(classesData.map(c => extractFormLevel(c.name)))].sort((a, b) => a.localeCompare(b));
        setForms(uniqueForms);

        // Fetch subjects
        const subjectsCollection = collection(db, `schools/${schoolID}/subjects`);
        const subjectsSnapshot = await getDocs(subjectsCollection);
        const subjectsData: Subject[] = subjectsSnapshot.docs.map((doc) => ({
          subjectId: doc.id,
          ...doc.data(),
        } as Subject)).filter(s => s.name && s.name.trim() !== "");

        setSubjects(subjectsData);

        // Fetch groups
        const groupsCollection = collection(db, `schools/${schoolID}/studentGroups`);
        const groupsSnapshot = await getDocs(groupsCollection);
        const groupsData: StudentGroup[] = groupsSnapshot.docs.map(g => ({ id: g.id, ...(g.data() as any) }));
        setSavedGroups(groupsData);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast({
          title: "Data Fetch Failed",
          description: "Unable to load data.",
          variant: "destructive",
        });
      }
    };

    fetchData();
  }, [schoolID, hasValidClaims]);

  // Filtered groups
  const filteredGroups = useMemo(() => {
    const q = (groupSearchQuery || '').trim().toLowerCase();
    if (!q) return savedGroups;
    return savedGroups.filter(g => (
      (g.name || '').toLowerCase().includes(q) ||
      (g.subject || '').toLowerCase().includes(q) ||
      (g.form || '').toLowerCase().includes(q)
    ));
  }, [savedGroups, groupSearchQuery]);

  // Filtered students
  const filteredStudents = useMemo(() => {
    const q = (studentSearchQuery || '').trim().toLowerCase();
    if (!q) return allFormStudents;
    return allFormStudents.filter(s => (
      (s.name || '').toLowerCase().includes(q) ||
      (s.regNumber || '').toLowerCase().includes(q)
    ));
  }, [allFormStudents, studentSearchQuery]);

  const handleCreateGroup = () => {
    setSelectedGroup(null);
    setGroupName('');
    setSelectedSubject('');
    setSelectedForm('');
    setAllFormStudents([]);
    setSelectedStudentIds([]);
    setStudentSearchQuery('');
    setIsEditMode(true);
  };

  const handleEditGroup = (group: StudentGroup) => {
    setSelectedGroup(group);
    setGroupName(group.name);
    setSelectedSubject(group.subject);
    setSelectedForm(group.form);
    setSelectedStudentIds(group.studentIds);
    setIsEditMode(true);
    // Load students for this form and subject
    loadStudentsForGroup(group.form, group.subject);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setSelectedGroup(null);
    setGroupName('');
    setSelectedSubject('');
    setSelectedForm('');
    setAllFormStudents([]);
    setSelectedStudentIds([]);
    setStudentSearchQuery('');
  };

  const handleDeleteClick = (group: StudentGroup) => {
    setGroupToDelete(group);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!groupToDelete) return;

    try {
      const groupRef = doc(db, `schools/${schoolID}/studentGroups`, groupToDelete.id);
      await deleteDoc(groupRef);

      setSavedGroups(prev => prev.filter(g => g.id !== groupToDelete.id));
      toast({
        title: "Group Deleted",
        description: `"${groupToDelete.name}" has been deleted successfully.`,
      });
    } catch (error) {
      console.error('Error deleting group:', error);
      toast({
        title: 'Failed to Delete Group',
        description: 'Please try again.',
        variant: 'destructive'
      });
    } finally {
      setShowDeleteDialog(false);
      setGroupToDelete(null);
    }
  };

  const loadStudentsForGroup = async (form: string, subject: string) => {
    if (!form || !subject) return;

    setIsFetching(true);
    try {
      const selectedSubjectItem = subjects.find(s => s.name === subject);
      if (!selectedSubjectItem) return;

      const formClasses = classes.filter(c => c.name.startsWith(form));
      if (formClasses.length === 0) return;

      const classUids = formClasses.map(c => c.classUid);
      let allStudents: Student[] = [];

      for (let i = 0; i < classUids.length; i += 10) {
        const batchUids = classUids.slice(i, i + 10);
        const studentsCollection = collection(db, `schools/${schoolID}/students`);
        const studentsQuery = query(studentsCollection, where("currentClassUid", "in", batchUids));
        const studentsSnapshot = await getDocs(studentsQuery);

        const batchStudents = studentsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: `${data.firstName || ''} ${data.lastName || ''}`.trim(),
            regNumber: data.registrationNumber || '',
            formClass: classes.find(cls => cls.classUid === data.currentClassUid)?.name || form,
            currentClassUid: data.currentClassUid,
            enrolledSubjects: data.enrolledSubjects || [],
            form: form
          } as Student;
        }).filter(s =>
          s.name &&
          s.regNumber &&
          s.enrolledSubjects?.includes(selectedSubjectItem.subjectId)
        );

        allStudents = [...allStudents, ...batchStudents];
      }

      setAllFormStudents(allStudents);
    } catch (error) {
      console.error("Error loading students:", error);
      toast({
        title: "Failed to Load Students",
        description: "Unable to load students.",
        variant: "destructive",
      });
    } finally {
      setIsFetching(false);
    }
  };

  const handleFormOrSubjectChange = async () => {
    if (!selectedForm || !selectedSubject) {
      setAllFormStudents([]);
      return;
    }
    await loadStudentsForGroup(selectedForm, selectedSubject);
  };

  useEffect(() => {
    if (selectedForm && selectedSubject && isEditMode) {
      handleFormOrSubjectChange();
    }
  }, [selectedForm, selectedSubject]);

  const handleSaveGroup = async () => {
    if (!groupName.trim()) {
      toast({
        title: "Group Name Required",
        description: "Please enter a name for the group.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedSubject || !selectedForm) {
      toast({
        title: "Subject and Form Required",
        description: "Please select both subject and form.",
        variant: "destructive",
      });
      return;
    }

    if (selectedStudentIds.length === 0) {
      toast({
        title: "No Students Selected",
        description: "Please select at least one student.",
        variant: "destructive",
      });
      return;
    }

    try {
      if (selectedGroup) {
        // Update existing group
        const groupRef = doc(db, `schools/${schoolID}/studentGroups`, selectedGroup.id);
        await updateDoc(groupRef, {
          name: groupName.trim(),
          subject: selectedSubject,
          form: selectedForm,
          studentIds: selectedStudentIds,
          updatedAt: serverTimestamp(),
        });

        setSavedGroups(prev => prev.map(g =>
          g.id === selectedGroup.id
            ? { ...g, name: groupName.trim(), subject: selectedSubject, form: selectedForm, studentIds: selectedStudentIds }
            : g
        ));

        toast({
          title: "Group Updated",
          description: `"${groupName}" has been updated successfully.`,
        });
      } else {
        // Create new group
        const groupsCollection = collection(db, `schools/${schoolID}/studentGroups`);
        const ref = await addDoc(groupsCollection, {
          name: groupName.trim(),
          subject: selectedSubject,
          form: selectedForm,
          studentIds: selectedStudentIds,
          createdAt: serverTimestamp(),
          createdBy: userID,
        });

        const newGroup: StudentGroup = {
          id: ref.id,
          name: groupName.trim(),
          subject: selectedSubject,
          form: selectedForm,
          studentIds: selectedStudentIds,
        };

        setSavedGroups(prev => [...prev, newGroup]);

        toast({
          title: "Group Created",
          description: `"${groupName}" has been created successfully.`,
        });
      }

      handleCancelEdit();
    } catch (error) {
      console.error('Error saving group:', error);
      toast({
        title: 'Failed to Save Group',
        description: 'Please try again.',
        variant: 'destructive'
      });
    }
  };

  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudentIds(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedStudentIds.length === filteredStudents.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(filteredStudents.map(s => s.id));
    }
  };

  // Show suspension warning if teacher is suspended
  if (isSuspended) {
    return <SuspensionWarning schoolName={suspendedSchoolName} isLoading={suspensionLoading} />;
  }

  if (isAuthLoading || !hasValidClaims || suspensionLoading) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-gray-50 dark:bg-gray-900">
          <Teachersidebar />
          <div className="flex flex-col flex-1">
            <Teacherheader />
            <main className="flex-grow p-3 sm:p-6 bg-gray-50 dark:bg-gray-900 flex justify-center items-center">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 dark:bg-gray-900 font-plus-jakarta">
        <Teachersidebar />
        <div className="flex flex-col flex-1 h-full">
          <Teacherheader />
          <main className="flex-1 overflow-y-auto p-2 bg-[#FFFFFF] dark:bg-[#020817] font-plus-jakarta">
            <div className="space-y-4 m-2">
              {!isEditMode ? (
                <>
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/dashboard/grading')}
                        className="hover:bg-gray-100 dark:hover:bg-[#131E31] border border-gray-300 dark:border-gray-600"
                      >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back
                      </Button>
                      <div className="flex items-center gap-2">
                        <Users className="h-5 w-5 md:h-6 md:w-6 text-emerald-600" />
                        <h1 className="text-2xl md:text-3xl font-source-sans font-bold leading-tight text-gray-900 dark:text-white">
                          Student Groups Management
                        </h1>
                      </div>
                    </div>
                    <Button
                      onClick={handleCreateGroup}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create New Group
                    </Button>
                  </div>

                  {/* Search and Filter Bar */}
                  <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                    <CardContent className="pt-6">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search groups by name, subject, or form..."
                          value={groupSearchQuery}
                          onChange={(e) => setGroupSearchQuery(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  {/* Groups Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredGroups.length > 0 ? (
                      filteredGroups.map(group => (
                        <Card key={group.id} className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] hover:shadow-lg transition-shadow">
                          <CardHeader>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                  {group.name}
                                </CardTitle>
                                <div className="space-y-1">
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800">
                                    {group.subject}
                                  </Badge>
                                  <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/30 dark:text-purple-300 dark:border-purple-800 ml-2">
                                    {group.form}
                                  </Badge>
                                </div>
                              </div>
                              <BookOpen className="h-5 w-5 text-emerald-600" />
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-3">
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Users className="h-4 w-4" />
                                <span>{group.studentIds.length} students</span>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditGroup(group)}
                                  className="flex-1 border border-gray-300 dark:border-gray-600"
                                >
                                  <Edit className="h-3 w-3 mr-1" />
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleDeleteClick(group)}
                                  className="flex-1"
                                >
                                  <Trash2 className="h-3 w-3 mr-1" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    ) : (
                      <div className="col-span-full">
                        <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                          <CardContent className="pt-12 pb-12">
                            <div className="text-center">
                              <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                {groupSearchQuery ? 'No Groups Found' : 'No Groups Yet'}
                              </h3>
                              <p className="text-sm text-muted-foreground mb-4">
                                {groupSearchQuery
                                  ? 'No groups match your search criteria.'
                                  : 'Create your first student group to get started.'}
                              </p>
                              {!groupSearchQuery && (
                                <Button
                                  onClick={handleCreateGroup}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Create Your First Group
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Edit/Create Form */}
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCancelEdit}
                          className="hover:bg-gray-100 dark:hover:bg-[#131E31]"
                        >
                          <ArrowLeft className="h-4 w-4 mr-2" />
                          Back to Groups
                        </Button>
                        <div className="flex items-center gap-2">
                          <Users className="h-5 w-5 md:h-6 md:w-6 text-emerald-600" />
                          <h1 className="text-2xl md:text-3xl font-source-sans font-bold leading-tight text-gray-900 dark:text-white">
                            {selectedGroup ? 'Edit Group' : 'Create New Group'}
                          </h1>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleSaveGroup}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          disabled={!groupName.trim() || !selectedSubject || !selectedForm || selectedStudentIds.length === 0}
                        >
                          {selectedGroup ? 'Update Group' : 'Create Group'}
                        </Button>
                      </div>
                    </div>

                    {/* Form Content */}
                    <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                      <CardHeader>
                        <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">Group Details</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Group Name */}
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Group Name</label>
                          <Input
                            placeholder="e.g., Advanced Mathematics Group"
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]"
                          />
                        </div>

                        {/* Subject and Form Selection */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Subject</label>
                            <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                              <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]">
                                <SelectValue placeholder="Select Subject" />
                              </SelectTrigger>
                              <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                                {subjects.map(subject => (
                                  <SelectItem key={subject.subjectId} value={subject.name} className="font-medium text-black dark:text-white">
                                    {subject.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Class/Form</label>
                            <Select value={selectedForm} onValueChange={setSelectedForm}>
                              <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]">
                                <SelectValue placeholder="Select Class" />
                              </SelectTrigger>
                              <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                                {forms.map(form => (
                                  <SelectItem key={form} value={form} className="font-medium text-black dark:text-white">
                                    {form}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Students Selection */}
                    {selectedForm && selectedSubject && (
                      <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                              Select Students ({selectedStudentIds.length} selected)
                            </CardTitle>
                            <div className="relative w-64">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input
                                placeholder="Search students..."
                                value={studentSearchQuery}
                                onChange={(e) => setStudentSearchQuery(e.target.value)}
                                className="pl-10 h-9"
                              />
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {isFetching ? (
                            <div className="flex items-center justify-center p-8">
                              <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                              <span className="ml-3 text-muted-foreground">Loading students...</span>
                            </div>
                          ) : allFormStudents.length > 0 ? (
                            <div className="border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg">
                              <div className="p-3 border-b border-[#E2E8F0] dark:border-[#1E293B] bg-muted/50">
                                <div className="flex items-center gap-2">
                                  <Checkbox
                                    checked={selectedStudentIds.length === filteredStudents.length && filteredStudents.length > 0}
                                    onCheckedChange={toggleSelectAll}
                                    className="border-[#E2E8F0] dark:border-[#1E293B]"
                                  />
                                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Select All Students ({filteredStudents.length})
                                  </label>
                                </div>
                              </div>
                              <div className="max-h-96 overflow-y-auto p-2 space-y-1">
                                {filteredStudents.map(student => (
                                  <div
                                    key={student.id}
                                    className="flex items-center gap-3 p-2 hover:bg-muted/50 rounded-lg transition-colors cursor-pointer"
                                    onClick={() => toggleStudentSelection(student.id)}
                                  >
                                    <Checkbox
                                      checked={selectedStudentIds.includes(student.id)}
                                      onCheckedChange={() => toggleStudentSelection(student.id)}
                                      onClick={(e) => e.stopPropagation()}
                                      className="border-[#E2E8F0] dark:border-[#1E293B]"
                                    />
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                                        {student.name}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {student.regNumber} • {student.formClass}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                                {filteredStudents.length === 0 && (
                                  <div className="text-center p-6 text-muted-foreground">
                                    No students found matching your search.
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-center p-8 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg">
                              <p className="text-muted-foreground">
                                No students found for {selectedForm} enrolled in {selectedSubject}.
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-white">
              Delete Group
            </DialogTitle>
            <DialogDescription className="text-sm font-medium text-gray-600 dark:text-gray-400">
              Are you sure you want to delete "{groupToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
};

export default GroupsManagement;
